import {
	convertUiPropertyToSceneCommand,
	convertUiValueToSceneValue,
	convertUiWeatherToSceneCondition,
	createGuiDeviceId,
} from 'src/components/Scenes/helpers/utilities_ui_to_scenes';
import { _deepClone } from '../../../helpers/utilities';
import { is_device_cond_ui } from '../../../types/DeviceCondAndTriggerUI';
import {
	deviceZoneIdToSceneZoneId,
	buildPresenceConditionName,
	validatePresenceConditionData,
} from '../SceneWizard/Process/When/AddEditContionWizard/Steps/DeviceBased/helpers/presence-helpers';
import {
	buildCameraSceneToken,
	CAMERA_COND_NAMES,
} from '../SceneWizard/Process/When/AddEditContionWizard/Steps/DeviceBased/helpers/camera-scene-helpers';
import type { DeviceConditionUI, TriggerTypeUI } from '../../../types/DeviceCondAndTriggerUI';
import type {
	ConditionOperator,
	DeviceAnyChangeTrigger,
	DeviceConditionOperatorAsKey,
	DeviceConditionOperatorInR,
	DeviceOnceTrigger,
	DeviceRepeatTrigger,
	SceneAsTrigger,
	SunriseSunsetTrigger,
	WeatherOnceTrigger,
	WeatherTriggerOnAnyChange,
} from '../../../types/ScenesConditionsTypes';
import { is_daily_data, is_execute_once } from '../../../types/TimeBasedConditionsUI';
import type {
	TimeBasedConditionsUI,
	DailyWeeklyCondition,
	ExecuteOnceAt,
} from 'src/types/TimeBasedConditionsUI';
import type { SunriseSunsetConditionUI } from 'src/types/SunriseSunsetConditionUI';
import { is_weather_cond_ui } from 'src/types/WeatherConditionUI';
import type { WeatherConditionUI } from 'src/types/WeatherConditionUI';
import { isVCType } from 'src/components/Scenes/helpers/virtual_components/virtual_component_utils';
import type { MathConditionUI as MathCondUI } from 'src/types/Scenes/MathConditionUI';

// const trigger_object = { rr: 'dt:t' };

// //HELPERS
// // time based conditions
export const createTimeBasedCondition = (data: TimeBasedConditionsUI) => {
	switch (data._gui_type) {
		case 'timer':
			return createTimer(<number>data.value);
		case 'd_cond':
			return createDaily(data.value as DailyWeeklyCondition);
		case 'ot_cond':
			return createOneTimeExecute(data.value as ExecuteOnceAt);
		default:
			return undefined;
	}
};

//time based conditions
const createTimer = (value: number = 0) => {
	if (typeof value === 'number' && (value >= 0 || value <= 60)) {
		return {
			dminslimit: value,
			rrtrig: {
				rr: 'dt:t',
			},
			_gui_type: 'timer',
		};
	} else {
		return undefined;
	}
};

const createDaily = (value: DailyWeeklyCondition) => {
	if (!is_daily_data(value)) return undefined;
	value.isin.sort((a, b) => a - b);

	return {
		and: [
			{
				eq: [
					{
						rr: 'dt:t',
					},
					value.time,
				],
			},
			{
				wday: 'now',
				isin: value.isin.map((el) => Number(el) + 1).join(' '),
			},
		],
		_gui_type: 'd_cond',
	};
};

const createOneTimeExecute = (value: ExecuteOnceAt) => {
	if (!is_execute_once(value)) return undefined;
	let { date, time } = value;
	if (time === '') return undefined;
	//User could set time, but doesn't change current date,
	//so we haven't this prop here, but the input field shows the current date
	if (date === undefined || date === '') {
		date = new Date().toISOString().split('T')[0];
	}
	let startTime = `${date} ${time}`;

	return {
		eq: [
			{
				rr: 'dt:dt',
			},
			startTime,
		],

		_gui_type: 'ot_cond',
	};
};

// device based condition with condition type (on any, repeat, once detect)
/** data.translated =  {
	*  value: operator === 'eq' ? translate_cond.value : OPERATORS.find(el => el[operator])[operator],
	   input: operator !== 'eq' ? translate_cond.value : undefined,
	   operator: OPERATORS.find(el => el[operator])[operator], 
	   device: {  id: deviceHex, channel},
	   addOnId: translate_cond.addOnId,
	   id: translate_cond.name, // will match condition id from _init_device_condition config
	   cond_type: {  id: condType,  holdOff }
   }
   data.id = deviceUI id 
   data.mode = device operation mode
   data.unitsConversion: undefined -> for temperature condition -> F/C
// */

//@ToDo: As of VC components concept in Scenes, refactor this function types by extracting them
const ui_device_to_scene_device = (
	data: DeviceConditionUI,
	more: { dev_mode: string | undefined; units_conversion: boolean },
	additionalData?: { channel?: string },
): {
	cond_by_dev_id: string;
	operator: ConditionOperator;
	value: string | number;
	_gui_device_id?: string; //Gui device id to preserve reference to the interface device id
} => {
	let _gui_device_id: string | undefined = undefined;
	let backEndID: number | string = data?.device?.id?.toLocaleLowerCase().startsWith('x')
		? data?.device?.id
		: parseInt(data?.device?.id, 16);
	if (
		(typeof backEndID === 'number' && isNaN(backEndID)) ||
		(typeof backEndID === 'string' && backEndID == '')
	)
		return undefined;

	let operator: ConditionOperator =
		([
			{ title: 'less_than', op: 'lt' },
			{ title: 'more_than', op: 'gt' },
			{ title: 'lt', op: 'lt' },
			{ title: 'gt', op: 'gt' },
		].find((el) => el.title === data.operator)?.op as any) || ('eq' as any);

	/**Extract id for Scene Service channel/index creation based on device, add on or 3EM meter ind which is channel id */
	let ch = Number(data.device.channel) || 0;

	if (
		typeof data.add_info_ind !== 'undefined' &&
		[
			'temperature',
			'humidity',
			'turn_add_on',
			'vol_meter_add_on',
			'analog_input_add_on',
			'push_type_add_on',
			'digital_input_add_on',
		].includes(data.cond_name)
	) {
		ch = Number(data.add_info_ind);
	}
	if (
		['meter', 'vol_meter', 'input_value', 'push_type'].includes(data.cond_name) &&
		typeof data.input_index !== 'undefined'
	) {
		ch = Number(data.input_index);
	}
	// Is Virtual Component condition
	if (isVCType(data?.cond_name)) {
		//Channel is the virtual component id
		if (typeof data.input_index !== 'undefined') {
			ch = data.input_index;
		}

		//Override the displayed device id for the scene's UI (preview/edit) with the interface device id
		_gui_device_id = createGuiDeviceId(data.device);
	}

	//Remote control channel
	if (data.cond_name === 'remote_control_channel') {
		ch = Number(data.value.split('_')[1]);
	}
	// Note: do not modify ch for scroll/push here. For push, ch comes from input_index (button 1/2).
	// For scroll, base trigger does not depend on ch. Channel suffix/value are handled below.

	// Build base scene condition (without optional channel suffix)
	let baseSceneCondition = convertUiPropertyToSceneCommand(data.cond_name, ch, more.dev_mode);
	let wantedValue = operator !== 'eq' ? Number(data.value) : data.value;

	// Convert UI value to Scene Service value based on base trigger
	let parsedValue = convertUiValueToSceneValue(baseSceneCondition, wantedValue);

	// Append optional channel rules for SBRC-005B (remote control)
	// - push_type: base 'ie1'; with channel -> 'ie1c' and thsval 'S<channel>'
	// - scroll_position: base 'rotie1'; with channel -> 'rotie1c' and thsval '<L|R><channel>'
	let sceneCondition = baseSceneCondition;
	if (
		(data.cond_name === 'push_type' || data.cond_name === 'scroll_position') &&
		additionalData?.channel &&
		additionalData.channel !== 'any'
	) {
		const chNum = Number(additionalData.channel.split('_')[1]);
		sceneCondition = `${baseSceneCondition}c`;
		if (data.cond_name === 'push_type') {
			parsedValue = `S${chNum}`;
		} else if (data.cond_name === 'scroll_position') {
			parsedValue = `${parsedValue}${chNum}`;
		}
	}

	let parse_wanted_to_scenes_val = parsedValue;

	if (import.meta.env.DEV) {
		console.log(
			'UI Cond to Scenes: transformed to iotS: ',
			sceneCondition,
			' operator: ',
			operator,
			' value: ',
			parse_wanted_to_scenes_val,
		);
	}

	// DEBUG: Log all data for presence-related conditions
	if (data.cond_name?.includes('presence') || data.cond_name?.startsWith('prsn_')) {
		console.log('DEBUG: Presence condition detected', {
			cond_name: data.cond_name,
			add_info_ind: data.add_info_ind,
			add_info_ind_type: typeof data.add_info_ind,
			sceneCondition: sceneCondition,
			data: data,
		});
	}

	// Special handling for S4SN-0U61X presence zone conditions using helper functions
	if (
		(data.cond_name === 'presence_zone_state' ||
			data.cond_name === 'presence_zone_objects' ||
			data.cond_name === 'presence_zone_objects_delta' ||
			data.cond_name?.startsWith('prsn_st4') ||
			data.cond_name?.startsWith('prsn_nobj_d4') ||
			data.cond_name?.startsWith('prsn_nobj4')) &&
		typeof data.add_info_ind !== 'undefined'
	) {
		let conditionType: 'st' | 'nobj' | 'nobj_d';
		let conditionName = data.cond_name;

		// Determine condition type
		if (conditionName === 'presence_zone_state' || conditionName?.startsWith('prsn_st4')) {
			conditionType = 'st';
			conditionName = 'presence_zone_state'; // Normalize for logging
		} else if (
			conditionName === 'presence_zone_objects_delta' ||
			conditionName?.startsWith('prsn_nobj_d4')
		) {
			conditionType = 'nobj_d';
			conditionName = 'presence_zone_objects_delta'; // Normalize for logging
		} else if (
			conditionName === 'presence_zone_objects' ||
			conditionName?.startsWith('prsn_nobj4')
		) {
			conditionType = 'nobj';
			conditionName = 'presence_zone_objects'; // Normalize for logging
		} else {
			console.warn('DEBUG: Unknown presence condition type', { data });
		}

		// Build the proper scene condition name using helper function
		const newSceneCondition = buildPresenceConditionName(conditionType, data.add_info_ind);

		// Validate before transformation
		const isValid = validatePresenceConditionData(
			{
				conditionName: conditionName,
				deviceZoneId: data.add_info_ind?.toString(),
				value: parsedValue,
				operator: operator,
			},
			'ui_conditions_to_scene.forward_mapping',
		);

		if (!isValid) {
			console.error('Forward mapping validation failed, using original newSceneCondition', {
				data,
				newSceneCondition,
				originalSdrCond: sceneCondition,
			});
		} else {
			console.log('DEBUG: Presence condition transformation', {
				originalCondName: data.cond_name,
				normalizedCondName: conditionName,
				conditionType,
				deviceZoneId: data.add_info_ind,
				sceneZoneId: deviceZoneIdToSceneZoneId(data.add_info_ind),
				oldSdrCond: sceneCondition,
				newSceneCondition: newSceneCondition,
			});

			sceneCondition = newSceneCondition;
		}
	} else if (
		data.cond_name === 'presence_zone_state' ||
		data.cond_name === 'presence_zone_objects' ||
		data.cond_name === 'presence_zone_objects_delta'
	) {
		console.warn('DEBUG: Presence condition found but add_info_ind is undefined', {
			cond_name: data.cond_name,
			add_info_ind: data.add_info_ind,
			add_info_ind_type: typeof data.add_info_ind,
			data: data,
		});
	}

	if (data.cond_name === CAMERA_COND_NAMES.ZONE_MOTION && typeof data.add_info_ind !== 'undefined') {
		sceneCondition = buildCameraSceneToken(CAMERA_COND_NAMES.ZONE_MOTION, data.add_info_ind);
	}

	return {
		cond_by_dev_id: `dev:${backEndID}:${sceneCondition}`,
		operator: operator,
		value: parse_wanted_to_scenes_val,
		_gui_device_id,
	};
};

// @ToDo: Refactor as of VC componets: extract type
const createOnceDevTr = (
	data_for_scenes: {
		cond_by_dev_id: string;
		operator: ConditionOperator;
		value: string | number;
	},
	hold_off: number | string | undefined,
): DeviceOnceTrigger | undefined => {
	let obj: DeviceOnceTrigger = {
		rr: data_for_scenes.cond_by_dev_id,
		thsop: data_for_scenes.operator,
		thsval: data_for_scenes.value,
		_gui_purpose: 'condition',
	};

	if (!isNaN(Number(hold_off)) && Number(hold_off) > 0) {
		(obj as DeviceOnceTrigger).hold_off = Number(hold_off);
	}
	return obj;
};

// @ToDo: extract type
const createOnAnyChangeDevTr = (data_for_scenes: {
	cond_by_dev_id: string;
	operator: string;
	value: string | number;
}): DeviceAnyChangeTrigger | undefined => {
	let obj: DeviceAnyChangeTrigger = {
		[data_for_scenes.operator as string]: [
			{
				rr: data_for_scenes.cond_by_dev_id,
			},
			data_for_scenes.value,
		],
		_gui_purpose: 'condition',
	};

	return obj;
};

// @ToDo: extract type
const createRepeatDevTr = (data_for_scenes: {
	cond_by_dev_id: string;
	operator: string;
	value: string | number;
}): DeviceRepeatTrigger | undefined => {
	let obj: DeviceRepeatTrigger = {
		[data_for_scenes.operator as string]: [
			{
				rr: data_for_scenes.cond_by_dev_id,
			},
			data_for_scenes.value,
		],
		_gui_purpose: 'condition',
		rrtrig: { rr: 'dt:t' },
	};

	return obj;
};
/** New format for device triggers  */
export const createDeviceTrigger = (
	data: DeviceConditionUI,
	cond_type: TriggerTypeUI,
	more: { dev_mode: undefined | string; units_conversion: boolean },
	additionalData?: { channel?: string },
): DeviceAnyChangeTrigger | DeviceRepeatTrigger | DeviceOnceTrigger | undefined => {
	if (!is_device_cond_ui(data) || !['once', 'repeat', 'any'].includes(cond_type.cond_type_name)) {
		return undefined;
	}
	let data_for_scenes = ui_device_to_scene_device(data, more, additionalData);
	let iots_condition = undefined;

	/** And here we prepare the trigger by selected type - once,any, repeat */
	switch (cond_type.cond_type_name) {
		case 'once':
			iots_condition = createOnceDevTr(data_for_scenes, cond_type.valid_for_min);
			break;
		case 'any':
			iots_condition = createOnAnyChangeDevTr(data_for_scenes);
			break;
		case 'repeat':
			iots_condition = createRepeatDevTr(data_for_scenes);
			break;
	}

	// When device_id is overriden, write it to the scene condition object
	if (data_for_scenes?._gui_device_id) {
		iots_condition._gui_device_id = data_for_scenes._gui_device_id;
	}

	return iots_condition;
};

/**New format for device conditions */
export const createDeviceCondition = (
	data: DeviceConditionUI,
	more: { dev_mode: undefined | string; units_conversion: boolean },
	additionalData?: { channel?: string },
): DeviceConditionOperatorAsKey | DeviceConditionOperatorInR => {
	if (!is_device_cond_ui(data)) return undefined;
	let map_data_to_scenes = ui_device_to_scene_device(data, more, additionalData);
	/**and here we make the device condition only format*/

	// {
	// 	*  'operator_as_key': [
	// 	*      { r: 'dev:decimalId:condByCh'},
	// 	*      desiredValue
	// 	*  ],
	// 	*  _gui_purpose: 'condition' //const,
	// 	* }

	//prefered for me, but not for the service
	// return {
	// 	[map_data_to_scenes.operator as OPERATOR]: [
	// 		{ r: map_data_to_scenes.cond_by_dev_id },
	// 		map_data_to_scenes.value
	// 	],
	// 	_gui_purpose: 'condition'
	// };

	let iots_condition: DeviceConditionOperatorInR = {
		r: map_data_to_scenes.cond_by_dev_id,
		cmpop: map_data_to_scenes.operator as ConditionOperator,
		cmpval: map_data_to_scenes.value,
	};

	if (map_data_to_scenes?._gui_device_id) {
		iots_condition._gui_device_id = map_data_to_scenes._gui_device_id;
	}
	return iots_condition;
};

/** scenes triggers */
export const createSceneAsTrigger = (automation_ui_data: {
	scene_id: '';
	selected: true;
}): SceneAsTrigger | undefined => {
	if (!automation_ui_data.scene_id) return undefined;

	return {
		rr: `nt:triggered_do:${automation_ui_data.scene_id}`,
		cmpop: 'eq',
		cmpval: 'true',
	};

	// return scene_as_trigger;
};

export const condition = (
	on = 'device',
	data: any = { id: '', mode: '', translated: '', unitsConversion: undefined },
) => {
	//tempID is temporary based on id => if type is device, then this is the device id
	let newCondition = undefined,
		obj = undefined;

	switch (on) {
		case 'weekly':
			newCondition = {
				indailyint: {
					dows: data?.translated?.days?.map((e) => Number(e) + 1).join(' '),
					startat: data?.translated?.startTime || '00:00',
					dur: data?.translated?.duration || 1440,
				},
			};

			break;
		default:
			console.warn('No data ', obj);
	}

	return newCondition;
};

/**
 *  {"rr":"owm:<locid>+<hour offset>:<cond>"}
 * example for temperature -> predicted temperature for current hour
 * owm:12312312+0:t
 */

export const createWeatherCondition = (
	data: WeatherConditionUI,
): WeatherTriggerOnAnyChange | WeatherOnceTrigger | undefined => {
	if (!is_weather_cond_ui(data)) return undefined;
	const { locid, hours } = data;

	const { condition, operator, value } = convertUiWeatherToSceneCondition(
		data.cond,
		data.op,
		data.value,
	);

	if (!condition || !operator || value === undefined || value === '') {
		return undefined;
	}

	const concatCondition = `owm:${locid}+${hours}:${condition}`;
	if (data.execution_type === 'any') {
		return {
			[operator]: [{ rr: concatCondition }, value],
			_gui_purpose: 'condition',
		};
	} else {
		return {
			rr: concatCondition,
			thsop: operator as ConditionOperator,
			thsval: value,
			_gui_purpose: 'condition',
		};
	}
};

const calculate_mins_and_hours_to_mins = (hours, min) => {
	return Number(60 * hours) + min;
};
/**
 * {rr:"sun:set:+/-"}
 ** {rr:"sun:rise:+/-"}
 */

export const create_sunrise_sunset_trigger = (data: SunriseSunsetConditionUI): SunriseSunsetTrigger => {
	let dur = calculate_mins_and_hours_to_mins(data.hours, data.mins);
	let sing = data.beforeAfter === 'after' ? '+' : '-';
	let at = data.type.substring(3);
	let locid = data.locid;
	return {
		rr: `sun:${locid}:${at}${sing}${dur}`,
	};
};

/**
 *
		"and": [
		  {
			"gt": [
			  {
			   "rr":"ppp:ibex:0"
			  },
			  133
			],
			"_gui_purpose": "condition",
			"rrtrig": {
			  "rr": "dt:t"
			}
		  }
		]
 * 
 * 
*/
export const create_exchange_based = (data: any) => {
	let { value, cond_name, exchange_market, operator: _operator, minutes = 0 } = data;

	let sceneCondition = cond_name === 'energy_exchange' ? 'ppp' : undefined;

	if (!sceneCondition) return undefined;

	if (sceneCondition === 'ppp') value = Number(value);

	if (isNaN(value)) return undefined;

	let operator: ConditionOperator =
		([
			{ title: 'less than', op: 'lt' },
			{ title: 'more than', op: 'gt' },
		].find((el) => el.title === _operator)?.op as any) || ('eq' as any);

	let rr = `${sceneCondition}:${exchange_market}:${minutes}`;

	return {
		[operator]: [
			{
				rr: rr,
			},
			value,
		],
		_gui_purpose: 'condition',
		rrtrig: { rr: 'dt:t' },
	};
};

/**
 * Create iots script for math condition
 * @param data
 * @returns {any} | undefined - iots script for math condition
 */
export const create_math_condition = (data: any) => {
	let { math_script, expression, params } = data;

	if (!math_script || !expression) return undefined;
	return {
		...math_script,
		_gui_type: 'math_based',
		_gui_purpose: 'condition',
		_gui_math_expression: expression,
		_gui_math_params: params,
	};
};

/**
 * Manual execution (no trigger condition)
 * @returns {any} - iots script for manual execution condition
 */
export const create_manual_execution_condition = (data: any) => {
	return {
		_gui_type: 'manual_execution',
		_gui_purpose: 'condition',
	};
};
